import type { DiffResult } from "@/lib/utils/patch-diff";
import type { PatchDiffResult } from "@/lib/utils/patch-diff";

/**
 * Check if a field key is an internal jsondiffpatch field
 * @param key The field key to check
 * @returns true if the field is internal and should be filtered out
 */
function isInternalField(key: string): boolean {
  // Filter out fields starting with _ (like _t, __old, __new)
  return key.startsWith("_");
}

/**
 * Check if a field path represents a simple string array field
 * Simple string arrays are arrays of strings (not objects), like divisions and specialities
 * @param path The field path to check
 * @returns true if the field is a simple string array (divisions or specialities)
 */
function isSimpleStringArrayField(path: string): boolean {
  if (!path) return false;
  // Extract the base field name (first part before any dots or brackets)
  const firstPart = path.split('.')[0];
  if (!firstPart) return false;
  const baseField = firstPart.split('[')[0];
  return baseField === "divisions" || baseField === "specialities";
}

/**
 * Normalize all array indices to wildcards
 * @param path The field path to normalize
 * @returns Normalized path with array indices replaced by .*
 * 
 * Examples:
 * - "weapons[0].ammo.heDamage" -> "weapons.*.ammo.heDamage"
 * - "divisions.0" -> "divisions.*"
 * - "divisions.0.name" -> "divisions.*.name"
 * - "specialities.1" -> "specialities.*"
 */
function normalizeArrayIndices(path: string): string {
  // Replace array indices in bracket notation: [0], [1], etc. -> .*
  let normalized = path.replace(/\[\d+\]/g, ".*");
  
  // Replace numeric keys in dot notation: .0, .1, etc. -> .*
  // This handles cases like "divisions.0" or "specialities.1"
  // Match a dot followed by digits, followed by either another dot or end of string
  normalized = normalized.replace(/\.\d+(?=\.|$)/g, ".*");
  
  // Clean up any duplicate .* patterns (e.g., ".*.*" -> ".*")
  normalized = normalized.replace(/\.\*\.\*/g, ".*");
  
  return normalized;
}

/**
 * Recursively collect all field paths from a diff object
 * @param diff The diff object to traverse
 * @param path The current path prefix (for nested fields)
 * @returns Set of all unique field paths found in the diff
 */
function collectFieldsFromDiff(diff: any, path: string = ""): Set<string> {
  const fields = new Set<string>();

  if (!diff || typeof diff !== "object") {
    return fields;
  }

  // jsondiffpatch commonly represents leaf diffs as:
  //   { __old: <value>, __new: <value> }
  // Those are not traversable via keys (we skip keys starting with `_`),
  // so we need to treat them as a value-change leaf and record `path`.
  if ("__old" in diff && "__new" in diff) {
    if (path) {
      fields.add(normalizeArrayIndices(path));
    }
    return fields;
  }

  // jsondiffpatch represents array element updates as an array diff marker like:
  // ['~', index, diff]
  // For weapons diffs we need to traverse the nested `diff` to expose weapon subfields
  // (ammo stats, traits, optics, etc.) in the filter UI.
  if (Array.isArray(diff) && diff.length > 0 && typeof diff[0] === "string") {
    const markerType = diff[0];
    const isWeaponPath =
      path === "weapons" ||
      path.startsWith("weapons.") ||
      path.startsWith("weapons[");

    if (isWeaponPath && markerType === "~") {
      // jsondiffpatch may represent a modification either as:
      // - ['~', index, diff]
      // - ['~', diff]   (index is implied by the surrounding array diff structure)
      const weaponModDiff = diff.length >= 3 ? diff[2] : diff[1];
      if (weaponModDiff && typeof weaponModDiff === "object") {
        const nestedFields = collectFieldsFromDiff(weaponModDiff, path);
        nestedFields.forEach((field) => fields.add(field));
      }
    }

    return fields;
  }

  // If it's a simple value change [oldValue, newValue], add the path
  if (Array.isArray(diff) && diff.length === 2 && typeof diff[0] !== "string") {
    if (path) {
      const normalizedPath = normalizeArrayIndices(path);
      fields.add(normalizedPath);
    }
    return fields;
  }

  // Traverse object properties
  Object.entries(diff).forEach(([key, value]) => {
    // Skip internal fields
    if (isInternalField(key)) {
      return;
    }
    
    // Handle array indices (numeric keys like "0", "1", etc.)
    // These can appear in two formats:
    // 1. As object keys: { "0": {...}, "1": {...} } -> path becomes "divisions.0"
    // 2. As array indices in bracket notation: path becomes "weapons[0]"
    if (typeof key === "string" && /^\d+$/.test(key)) {
      const parentPath = path || "";
      // Use dot notation for numeric keys (e.g., "divisions.0")
      const arrayIndexPath = parentPath ? `${parentPath}.${key}` : key;
      
      // Check if this is a simple string array field (divisions or specialities)
      if (isSimpleStringArrayField(parentPath)) {
        // For simple string arrays, just collect the parent field name without normalization
        // Don't collect "divisions.0" or "divisions.*" - just "divisions"
        if (parentPath) {
          fields.add(parentPath);
        }
        // Don't recurse into string values - they're not objects
      } else {
        // For nested object arrays (like weapons), normalize the path
        const normalizedPath = normalizeArrayIndices(arrayIndexPath);
        fields.add(normalizedPath);
        
        // Also ensure the parent field is collected (e.g., "weapons")
        if (parentPath) {
          const normalizedParentPath = normalizeArrayIndices(parentPath);
          fields.add(normalizedParentPath);
        }
        
        // Recursively collect from array element
        if (value && typeof value === "object") {
          const nestedFields = collectFieldsFromDiff(value, arrayIndexPath);
          nestedFields.forEach(field => {
            const normalizedField = normalizeArrayIndices(field);
            fields.add(normalizedField);
          });
        }
      }
    } else {
      const newPath = path ? `${path}.${key}` : key;
      
      // Check if this is a simple string array field (divisions or specialities)
      if (isSimpleStringArrayField(newPath)) {
        // For simple string arrays, collect the field name as-is without normalization
        fields.add(newPath);
        // Check if value is an object with _t: 'a' marker (jsondiffpatch array diff format)
        if (value && typeof value === "object" && !Array.isArray(value) && (value as any)._t === 'a') {
          // This is a jsondiffpatch array diff, field is already collected above
          return; // Don't recurse into the array diff structure
        }
        // Don't recurse into string values - they're not objects
      } else {
        // For other fields, normalize the path
        if (newPath) {
          const normalizedPath = normalizeArrayIndices(newPath);
          fields.add(normalizedPath);
        }

        // Recursively collect from nested object
        if (value && typeof value === "object" && !Array.isArray(value)) {
          // Check if this is a jsondiffpatch array diff (has _t: 'a' marker)
          // If so, we've already collected the field, don't recurse (except for weapons).
          if ((value as any)._t === 'a') {
            // jsondiffpatch array diffs store nested changes under numeric keys.
            // For `weapons` we need to traverse into those numeric keys to expose
            // weapon subfields (ammo stats, optics, etc.) in the filter UI.
            if (newPath.startsWith("weapons")) {
              const nestedFields = collectFieldsFromDiff(value, newPath);
              nestedFields.forEach((field) => {
                const normalizedField = normalizeArrayIndices(field);
                fields.add(normalizedField);
              });
            }
          } else {
            const nestedFields = collectFieldsFromDiff(value, newPath);
            nestedFields.forEach(field => {
              const normalizedField = normalizeArrayIndices(field);
              fields.add(normalizedField);
            });
          }
        } else if (Array.isArray(value) && value.length > 0) {
          // Check if this is an array of diff markers (like divisions: [['+', value], ['-', value]])
          const isArrayOfDiffMarkers = Array.isArray(value[0]) && 
                                       value[0] &&
                                       Array.isArray(value[0]) && 
                                       value[0].length >= 2 && 
                                       typeof value[0][0] === "string";
          
          if (isArrayOfDiffMarkers) {
            // This is an array of diff markers - ensure the parent field is collected
            // For simple string arrays, use the field name as-is
            if (isSimpleStringArrayField(newPath)) {
              fields.add(newPath);
            } else {
              const normalizedParentPath = normalizeArrayIndices(newPath);
              fields.add(normalizedParentPath);
            }

            // Special case: weapons array diffs
            // jsondiffpatch represents `weapons` changes as an array of diff markers like:
            // ['~', weaponIndex, diff]
            // We need to traverse these nested diffs to expose weapon subfields in the filter UI.
            if (newPath.startsWith("weapons")) {
              value.forEach((item) => {
                if (!Array.isArray(item) || item.length < 3) return;
                const markerType = item[0];
                // jsondiffpatch array diffs sometimes use:
                // - ['~', index, diff]
                // - ['~', diff] (rare for this representation, but handle anyway)
                const weaponIndex = item.length >= 3 ? item[1] : "*";
                const weaponModDiff = item.length >= 3 ? item[2] : item[1];

                if (markerType === "~" && weaponModDiff && typeof weaponModDiff === "object") {
                  const weaponIndexPath = weaponIndex === "*" ? `${newPath}.*` : `${newPath}.${weaponIndex}`;
                  const nestedFields = collectFieldsFromDiff(weaponModDiff, weaponIndexPath);
                  nestedFields.forEach((field) => fields.add(field));
                }
              });
            }
          } else {
            value.forEach((item, index) => {
              if (item && typeof item === "object") {
                // Check if it's a diff marker like ['+', value] or ['-', value]
                if (Array.isArray(item) && item.length >= 2 && typeof item[0] === "string") {
                  // It's a diff marker, the actual value is at index 1
                  if (item[1] && typeof item[1] === "object") {
                    const arrayItemPath = `${newPath}[${index}]`;
                    const nestedFields = collectFieldsFromDiff(item[1], arrayItemPath);
                    nestedFields.forEach(field => {
                      const normalizedField = normalizeArrayIndices(field);
                      fields.add(normalizedField);
                    });
                  }
                } else {
                  // Regular array element
                  const arrayItemPath = `${newPath}[${index}]`;
                  const nestedFields = collectFieldsFromDiff(item, arrayItemPath);
                  nestedFields.forEach(field => {
                    const normalizedField = normalizeArrayIndices(field);
                    fields.add(normalizedField);
                  });
                }
              }
            });
          }
        }
      }
    }
  });

  return fields;
}

/**
 * Collect all unique field paths from a patch diff result
 * @param patchDiff The patch diff result containing added, removed, and changed units
 * @returns Set of all unique field paths found in the diff
 */
export function collectAllFields(patchDiff: PatchDiffResult): Set<string> {
  const allFields = new Set<string>();

  // Collect from changed units
  patchDiff.changed.forEach((change) => {
    const fields = collectFieldsFromDiff(change.diff);
    fields.forEach(field => allFields.add(field));
  });

  // Note: We don't collect from added/removed units as they show all fields anyway
  // The filter only applies to changed units

  return allFields;
}

/**
 * Group fields by category for display
 * @param fields Set of field paths
 * @returns Object with categories as keys and arrays of fields as values
 */
export function groupFieldsByCategory(fields: Set<string>): {
  unit: string[];
  weapon: string[];
  ammo: string[];
  other: string[];
} {
  const unit: string[] = [];
  const weapon: string[] = [];
  const ammo: string[] = [];
  const other: string[] = [];

  fields.forEach((field) => {
    if (field.startsWith("weapons")) {
      if (field.includes(".ammo.")) {
        ammo.push(field);
      } else {
        weapon.push(field);
      }
    } else {
      // Check if it's a common unit field
      const unitFields = [
        "name", "id", "descriptorName", "commandPoints", "specialities",
        "divisions", "unitType", "factoryDescriptor", "weaponDescriptorName"
      ];
      if (unitFields.some(uf => field === uf || field.startsWith(uf + "."))) {
        unit.push(field);
      } else {
        other.push(field);
      }
    }
  });

  return { unit, weapon, ammo, other };
}




